function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState } from 'react';
import ClaimGroupPageContainer from '../containers/ClaimGroupPageContainer';
import { useTranslation } from 'react-i18next';
import claimGroupService from '../../services/claim_group.service';

/**
 * Usage:
 * const TechnicalNarrativesPage = AbstractClaimGroupChildPage(function ({ accountType, handleToaster, claimGroup, setClaimGroup }) {
 *   return <>
 *     Hello from TechnicalNarrativesPage {claimGroup.id}
 *   </>
 * })
 * 
 * The TechnicalNarrativesPage page can then be added as normal to the App.js file.
 * 
 * This is a higher order component. Therefore it is a function that returns a function.
 * The returned function is the actual component called.
 * This is done in order to give us a way to reuse the same functionality for a page:
 *     - sidebars
 *     - breadcrumbs
 *     - default state variables - in this case claimGroup and setClaimGroup
 *     ... etc ...
 *
 * The ClaimGroupPageContainer already defines and executes the shared functionality.
 * However it is cumbersome to use it in a new claim group child page.
 * E.g. the usage would have to be something like this:
 * 
 * // some ClaimGroupChildPage component - e.g. ProjectOverviewPage
 * const [claimGroup, setClaimGroup] = useState(null)
 * return <>
 *   <ClaimGroupPageContainer accountType={accountType} claimGroup={claimGroup} setPageClaimGroup={setClaimGroup}>
 *     <div>HELLO { claimGroup?.id}</div>
 *   </ClaimGroupPageContainer>
 * </>
 * 
 * As you see we still need to define the state variables in the page component in order to make sure that the claimGroup exists
 * before we try to use it. And we still need to use the question mark operator here claimGroup?.id otherwise
 * we need to add if statements to check that the claimGroup is loaded etc. This makes the whole thing pointless.
 * The need to explicitly check the claimGroup with the question mark operator is because even if we add a check
 * inside the ClaimGroupPageContainer that renders the children only after the passedClaimGroup has an id,
 * e.g. if (!passedClaimGroup?.id) { return <Loading />; }
 * - it still evaluates this line <div>HELLO { claimGroup?.id}</div> - and throws an error.
 * Even though the check prevents rendering of the children, the line gets evaluated anyway. 
 * 
 * Therefore we create this wrapped component that calls the ClaimGroupPageContainer inside of itself and 
 * then inside of it it passes the ClaimGroupChildPage as the children property of ClaimGroupPageContainer.
 * With this setup the contents of ClaimGroupChildPage dont get evaluated, and at the same time we get a cleaner syntax.
 * 
 * const TechnicalNarrativesPage = AbstractClaimGroupChildPage(function ({ accountType, handleToaster, claimGroup, setClaimGroup }) {
 *   return <>
 *     Hello from TechnicalNarrativesPage {claimGroup.id}
 *   </>
 * })
 * 
 * @param {*} ClaimGroupChildPage 
 * @returns 
 */
export default function AbstractClaimGroupChildPage(_ref) {
  let {
    highlightedMenuItem,
    ClaimGroupChildPage
  } = _ref;
  return function WrappedPage(_ref2) {
    let {
      accountType,
      handleToaster,
      ...props
    } = _ref2;
    const {
      t
    } = useTranslation();
    const [claimGroup, setClaimGroup] = useState(null);
    const [pageIsLoading, setPageIsLoading] = useState(false);
    const resetClaimGroup = claimGroupService.useFetchClaimGroupAndUpdateTechnicalReviewState({
      claimGroupId: claimGroup?.id,
      setClaimGroup
    });
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClaimGroupPageContainer, {
      highlightedMenuItem: highlightedMenuItem,
      accountType: accountType,
      claimGroup: claimGroup,
      setClaimGroup: setClaimGroup
    }, /*#__PURE__*/React.createElement(ClaimGroupChildPage, _extends({
      accountType: accountType,
      handleToaster: handleToaster,
      claimGroup: claimGroup,
      setClaimGroup: setClaimGroup,
      resetClaimGroup: resetClaimGroup,
      pageIsLoading: pageIsLoading,
      setPageIsLoading: setPageIsLoading,
      t: t
      // most likely there wont be other props, but just in case
    }, props))));
  };
}